import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GAME_TOP_UP_CLOSE_EVENT, GAME_TOP_UP_EVENT, isGameRoute } from '../game-top-up.js';
import { useAppStore } from '../store.js';
import { restorePassPaymentReturn } from '../pass-payment-return.js';

const Store = lazy(() =>
  import('../screens/StoreScreen.js').then((module) => ({ default: module.StoreScreen })),
);

export function GameTopUpDialog() {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = useAppStore((state) => state.session?.user.internalId);
  const [open, setOpen] = useState(false);
  const [returnToArtifacts, setReturnToArtifacts] = useState(false);
  const dialog = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => {
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);
  const key = `bb:game-payment-return:${userId}`;
  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener(GAME_TOP_UP_CLOSE_EVENT, close);
    return () => window.removeEventListener(GAME_TOP_UP_CLOSE_EVENT, close);
  }, []);
  useEffect(() => {
    const show = (event: Event) => {
      if (!isGameRoute(location.pathname)) return;
      const detail: unknown = event instanceof CustomEvent ? event.detail : null;
      const artifacts = Boolean(
        detail &&
        typeof detail === 'object' &&
        'returnTo' in detail &&
        detail.returnTo === 'artifacts',
      );
      setReturnToArtifacts(artifacts);
      try {
        localStorage.setItem(
          key,
          JSON.stringify({
            path: location.pathname + location.search,
            at: Date.now(),
            returnToArtifacts: artifacts,
          }),
        );
      } catch {
        /* Storage may be unavailable. */
      }
      setOpen(true);
    };
    window.addEventListener(GAME_TOP_UP_EVENT, show);
    return () => window.removeEventListener(GAME_TOP_UP_EVENT, show);
  }, [key, location.pathname, location.search]);
  useEffect(() => {
    const payment = new URLSearchParams(location.search).get('payment');
    if (location.pathname !== '/store' || !payment || !/^[0-9a-f-]{36}$/i.test(payment)) return;
    if (userId && restorePassPaymentReturn(userId, payment)) {
      setOpen(false);
      void navigate('/?pass=open&passPayment=return', { replace: true });
      return;
    }
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
      if (
        !saved ||
        typeof saved !== 'object' ||
        !('path' in saved) ||
        typeof saved.path !== 'string' ||
        !('at' in saved) ||
        typeof saved.at !== 'number'
      )
        return;
      if (Date.now() - saved.at > 3 * 3_600_000 || !isGameRoute(saved.path.split('?')[0]!)) return;
      localStorage.setItem(`bb:pending-payment:${userId}`, payment);
      setReturnToArtifacts('returnToArtifacts' in saved && saved.returnToArtifacts === true);
      setOpen(true);
      void navigate(saved.path, { replace: true });
    } catch {
      /* Normal store recovery remains available. */
    }
  }, [key, location.pathname, location.search, navigate, userId]);
  if (!open || !isGameRoute(location.pathname)) return null;
  return (
    <div className="modal-backdrop game-top-up-backdrop">
      <section
        ref={dialog}
        tabIndex={-1}
        className="app-modal game-top-up-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Алмазы без выхода из игры"
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const elements = dialog.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), input:not(:disabled), a[href], select:not(:disabled), [tabindex="0"]',
          );
          const visible = Array.from(elements ?? []).filter(
            (element) => element.getClientRects().length > 0,
          );
          const first = visible[0],
            last = visible[visible.length - 1];
          if (
            event.shiftKey &&
            (document.activeElement === first || document.activeElement === dialog.current)
          ) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <Suspense fallback={<p>Загружаем пакеты…</p>}>
          <Store
            embedded
            closeLabel={returnToArtifacts ? 'К выбранному артефакту' : 'Вернуться к игре'}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      </section>
    </div>
  );
}
